import React, { useState, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import SalesDetailPage from './SalesDetailPage';
import {
  SHOW_TRACKER_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns,
} from '../constants/showTrackerColumnsConfig';
import { buildShowTrackerFilterGroups } from '../constants/showTrackerFiltersConfig';
import { getCellText, buildRowTsv } from './showTrackerRow';
import { toastSuccess, toastError } from '../../../shared/toastHelper';
import { copyText } from '@opendate-app/setlist';

const axios = require('axios').default;

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export { getCellText, buildRowTsv };

const EXPORTABLE_COLUMNS = SHOW_TRACKER_COLUMNS.filter(c => c.id !== '_copy');

const copyRowToClipboard = (event) =>
  copyText(buildRowTsv(EXPORTABLE_COLUMNS, event))
    .then((ok) => (ok ? toastSuccess('Copied to Clipboard') : toastError('Could not copy to clipboard')));

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) params.venue_name = activeFilters.venue_name;
  if (activeFilters.event_status && activeFilters.event_status.length > 0) params.event_status = activeFilters.event_status;
  return params;
};

const SHOW_TRACKER_CONFIG = {
  pageTitle: 'Show Tracker',
  pageSubtitle: 'All confirmed shows with announce, presale, and onsale timing — the marketing bible, auto-populated.',
  sidebarKey: 'marketing_show_tracker',
  defaultPreset: 'next_12_months',
  dateModeOptions: [{ key: 'event_date', label: 'Event Date' }],
  defaultSortRules: [{ field: 'event_date', direction: 'asc' }],
  scrollableTable: true,
  stickyColumns: [{ id: '_copy', width: 72 }, { id: 'event_name' }],
  pageSizeOptions: [20, 50, 100],
  detailOnly: true,

  columns: SHOW_TRACKER_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingMarketingShowTracker_v5_${teamId}`,
  firstColumnId: '_copy',

  kpiFromDetailResponse: false,
  buildKpiCards: () => [],
  kpiGridColumns: 0,
  salesDataKey: null,
  venueSeriesKey: null,
  overTimeKey: null,
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing/show-tracker/data.json`,
  detailResponseKey: 'events',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/marketing/show-tracker/export`,
  salesExportSection: 'show_tracker',
  detailErrorMessage: 'Failed to load show tracker data.',
  detailTableTitle: 'Show Tracker',
  emptyMessage: 'No confirmed shows found.',
  searchPlaceholder: 'Search shows...',
  paginationLabel: 'shows',
  itemLabel: (count) => `show${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  renderCellValue: (col, event) => {
    switch (col.id) {
      case '_copy':
        return (
          <button
            type="button"
            className="btn btn-sm p-0 border-0 bg-transparent"
            title="Copy row (tab-separated) for spreadsheet paste"
            aria-label="Copy row"
            style={{ color: 'var(--sl-text-tertiary, #667085)', lineHeight: 0 }}
            onClick={() => copyRowToClipboard(event)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M5 15C4.06812 15 3.60218 15 3.23463 14.8478C2.74458 14.6448 2.35523 14.2554 2.15224 13.7654C2 13.3978 2 12.9319 2 12V5.2C2 4.0799 2 3.51984 2.21799 3.09202C2.40973 2.71569 2.71569 2.40973 3.09202 2.21799C3.51984 2 4.07989 2 5.2 2H12C12.9319 2 13.3978 2 13.7654 2.15224C14.2554 2.35523 14.6448 2.74458 14.8478 3.23463C15 3.60218 15 4.06812 15 5M12.2 22H18.8C19.9201 22 20.4802 22 20.908 21.782C21.2843 21.5903 21.5903 21.2843 21.782 20.908C22 20.4802 22 19.9201 22 18.8V12.2C22 11.0799 22 10.5198 21.782 10.092C21.5903 9.71569 21.2843 9.40973 20.908 9.21799C20.4802 9 19.9201 9 18.8 9H12.2C11.0799 9 10.5198 9 10.092 9.21799C9.71569 9.40973 9.40973 9.71569 9.21799 10.092C9 10.5198 9 11.0799 9 12.2V18.8C9 19.9201 9 20.4802 9.21799 20.908C9.40973 21.2843 9.71569 21.5903 10.092 21.782C10.5198 22 11.0799 22 12.2 22Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        );
      case 'event_name': {
        if (!event.event_name) return '—';
        if (!event.event_url) return event.event_name;
        return <a href={event.event_url} target="_blank" rel="noopener noreferrer">{event.event_name}</a>;
      }
      case 'event_date': {
        const isToday = event.event_date_ymd === todayYmd();
        return (
          <span
            className="text-nowrap"
            style={isToday ? { background: 'var(--sl-fg-brand, #1982c4)', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 600 } : undefined}
          >
            {event.event_date || '—'}
          </span>
        );
      }
      case 'support': {
        const full = event.support || '';
        if (!full) return '—';
        return (
          <span
            title={full}
            className="d-inline-block text-truncate"
            style={{ maxWidth: 220, verticalAlign: 'bottom' }}
          >
            {full}
          </span>
        );
      }
      case 'url': {
        if (!event.url) return '—';
        return <a href={event.url} target="_blank" rel="noopener noreferrer">{event.url}</a>;
      }
      case 'website_url': {
        if (!event.website_url) return '—';
        return <a href={event.website_url} target="_blank" rel="noopener noreferrer">{event.website_url}</a>;
      }
      default:
        return getCellText(col, event) || '—';
    }
  },

  filterTitle: 'Filter Shows',
  filterSubtitle: 'Narrow the show tracker by venue and status.',
};

function ReportingMarketingShowTrackerPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/marketing/show-tracker/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => toastError('Could not load filter options'));
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...SHOW_TRACKER_CONFIG,
    buildFilterGroups: () => buildShowTrackerFilterGroups(filterOptions),
  };
  return (
    <>
      <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />
      <ToastContainer />
    </>
  );
}

export default ReportingMarketingShowTrackerPage;
